<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      v-if="!inPopup"
      no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          Zamówienia
        </span>
        <h3 class="page-title">Lista zamówień</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button theme="success"
          @click.prevent="$router.push('/zamowienia/nowe')">
          <i class="material-icons">add</i>
          Dodaj zamówienie
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="allDataLoaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="orders-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę zamówienia lub klienta"
          v-model="filters.search"
          :class="{ 'w-25': !inPopup, 'w-50': inPopup }" />

        <v-select
          v-if="!inPopup"
          class="ml-1 w-25"
          :options="orderWorkerOptions"
          v-model="filters.worker_id"
          placeholder="Wybierz pracownika"
          :searchable="true"
          label="text">
        </v-select>

        <v-select
          v-if="!inPopup"
          class="ml-1 w-25"
          :options="orderStatusOptions"
          v-model="filters.status"
          placeholder="Status zamówienia"
          label="text">
        </v-select>

        <date-range-picker
          ref="picker"
          :locale-data="{
            'firstDay': 1,
            'format': 'DD-MM-YYYY',
            'daysOfWeek': ['Ndz', 'Pon', 'Wto', 'Śro', 'Czw', 'Ptk', 'Sob'],
            'monthNames': ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
          }"
          class="ml-1 w-50"
          style="min-width: 200px;"
          :singleDatePicker="false"
          :timePicker="false"
          :showWeekNumbers="false"
          :showDropdowns="false"
          :autoApply="true"
          v-model="dateRange"
          :ranges="false"
          :linkedCalendars="true">
          <template v-slot:input="picker" style="min-width: 350px;">
            {{ picker.startDate | dateStart }} - {{ picker.endDate | dateEnd }}
          </template>
        </date-range-picker>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Nazwa zamówienia</th>
            <th scope="col" class="border-0">Klient</th>
            <th scope="col" class="border-0">Planowana wysyłka</th>
            <th scope="col" class="border-0">Produkty</th>
            <th scope="col" class="border-0">Stan</th>
            <th scope="col" class="border-0">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr
            v-for="(order, index) of orders"
            :key="'order-' + index"
            :class="{ 'has-unread-logs': order.has_unread_logs }">
            <td class="align-middle">
              <d-badge
                v-if="order.has_unread_logs"
                theme="danger"
                class="mr-2"
                title="Zamówienie posiada nieprzeczytane powiadomienia">
                !
              </d-badge>
              {{ order.order_name }}
            </td>
            <td class="align-middle">
              {{ order.customer_name }}
            </td>
            <td class="align-middle">
              <span :class="plannedShipmentDateColor(order.planned_shipment_date)">{{ order.planned_shipment_date }}</span>
            </td>
            <td class="align-middle">
              <d-badge
                @click.native="showOrderProducts(order.order_name, order.products)"
                class="cursor-pointer">
                {{ order.order_products_count }}
              </d-badge>
            </td>
            <td class="align-middle">
              <span :class="orderStatus(order)">
                {{ order.products_done_count }} / {{ order.products_with_procedure_count }}
              </span>
            </td>
            <td
              class="align-middle"
              style="width: 200px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/zamowienia/edycja/' + order.id)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="info"
                class="mr-2 text-nowrap"
                :disabled="downloadInProgress === order.id"
                @click.prevent.stop="downloadPdf(order.id)"
                title="Pobierz oznaczenie">
                <div
                  v-if="downloadInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-if="downloadInProgress !== order.id"
                  class="material-icons">
                  cloud_download
                </i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeOrder(order.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td
              colspan="6"
              class="text-center">
              Brak zamówień do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <d-modal
      v-if="showProductsPopup"
      size="lg"
      @close="showProductsPopup = false">
      <d-modal-header>
        <d-modal-title>
          Produkty w zamówieniu <span class="text-primary">{{ popupOrderName }}</span>
        </d-modal-title>
      </d-modal-header>
      <d-modal-body class="p-0 pb-3">
        <table class="table table-sm w-100">
          <thead>
            <tr>
              <th class="pl-4">Produkt</th>
              <th>Polar</th>
              <th>Rozmiar</th>
              <th>Ilość</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(product, index) of popupProducts"
              :key="'product-from-order-' + index">
              <td class="pl-4">
                {{ product.name }}
              </td>
              <td>
                {{ product.polar_name }}
              </td>
              <td>
                {{ product.size_name }}
              </td>
              <td>
                {{ product.quantity }} szt.
              </td>
            </tr>
          </tbody>
        </table>
      </d-modal-body>
    </d-modal>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import Vue from 'vue';

export default {
  name: 'orders',
  props: {
    inPopup: {
      default: false
    },
    workerID: {
      default: false
    }
  },
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination
  },
  filters: {
    dateStart (input) {
      if (input === null) {
        return ' szukaj od ';
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    dateEnd (input) {
      if (input === null) {
        return ' do ';
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    }
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers;
    },
    orderWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    orderStatusOptions () {
      return [{
        text: 'Dowolny',
        value: ''
      },
      {
        text: 'Nowe',
        value: 'NEW'
      },
      {
        text: 'Trwające',
        value: 'IN_PROGRESS'
      },
      {
        text: 'Wysłano do rozliczenia',
        value: 'SENT_TO_SETTLEMENT'
      },
      {
        text: 'Ukończone',
        value: 'DONE'
      },
      {
        text: 'Anulowane',
        value: 'CANCELLED'
      }];
    }
  },
  data() {
    return {
      orders: [],
      loaded: false,
      loadedWorkers: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: '',
        status: null,
        worker_id: null
      },
      dateRange: {
        startDate: null,
        endDate: null
      },
      downloadInProgress: false,
      workers: [],
      popupProducts: [],
      popupOrderName: '',
      showProductsPopup: false
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      if (this.filters.worker_id) {
        where.worker_id = parseInt(this.filters.worker_id.value, 10);
      }

      if (this.filters.status && this.filters.status.value) {
        where.status = this.filters.status.value;
      }

      if (this.workerID) {
        where.worker_id = parseInt(this.workerID, 10);
      }

      if (this.dateRange.startDate) {
        where.planned_shipment_from = this.prepareDateString(this.dateRange.startDate);
      }

      if (this.dateRange.endDate) {
        where.planned_shipment_to = this.prepareDateString(this.dateRange.endDate);
      }

      ListUtils.loadItemsData(this, {
        endpoint: '/orders/items?page=' + this.currentPage,
        listField: 'orders',
        pagination: true,
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        outputKey: 'workers',
        loadedKey: 'loadedWorkers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            type: 'WORKER'
          }
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        status: null,
        worker_id: null
      };
      this.dateRange.startDate = null;
      this.dateRange.endDate = null;
      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['orders-filters-form'], this.debouncedLoadFilteredData);
    },
    removeOrder (orderID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/orders/delete/',
        id: orderID,
        successTitle: 'Usunięto zamówienie',
        successText: 'Wybrane zamówienie zostało usunięte',
        confirmTitle: 'Czy na pewno chcesz usunąć to zamówienie?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie zamówienia nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    plannedShipmentDateColor (dateToCheck) {
      let timestamp = +new Date(dateToCheck);
      let currentTimestamp = +new Date();

      if (timestamp - currentTimestamp < 0) {
        return 'text-danger font-weight-bold';
      } else if (timestamp - currentTimestamp < 4 * 24 * 60 * 60 * 1000) {
        return 'text-warning';
      }

      return 'text-primary';
    },
    orderStatus (orderData) {
      if (orderData.products_with_procedure_count < orderData.order_products_count) {
        return 'text-danger';
      } else if (
        orderData.products_with_procedure_count === orderData.order_products_count &&
        orderData.products_done_count === orderData.order_products_count
      ) {
        return 'text-success';
      }

      return 'text-dark';
    },
    showOrderProducts (name, products) {
      this.popupOrderName = name;
      this.popupProducts = JSON.parse(JSON.stringify(products));
      this.showProductsPopup = true;
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    downloadPdf (orderID) {
      this.downloadInProgress = orderID;
      this.$http.post('/orders/box-marking/' + orderID, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = 'oznaczenie-zamowienie-';
        link.setAttribute('download', filenamePrefix + orderID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.downloadInProgress = false;
      });
    },
  }
};
</script>

<style lang="scss">
tr.has-unread-logs td {
  background: rgba(255, 0, 0, .125);
}
</style>
